/**
 * Pixel-density math.
 *
 * Lives in the tool rather than in `core-net`: that package is network math only.
 * Everything here is still pure and DOM-free, so it is unit-tested in isolation —
 * no `.vue` file may re-implement any of it.
 */

export const LENGTH_UNITS = ['in', 'cm'] as const
export type LengthUnit = (typeof LENGTH_UNITS)[number]

const MM_PER_INCH = 25.4
const CM_PER_INCH = 2.54

/** Largest side a reduced ratio may have before we snap to a common one instead. */
const MAX_REDUCED_SIDE = 32

/**
 * Android's density buckets, the same scale most "is this screen sharp?" advice is
 * written against. A display is labelled with its nearest bucket, not an exact
 * match — real panels rarely land on one.
 * https://developer.android.com/training/multiscreen/screendensities
 */
export const DENSITY_BUCKETS = [
  { key: 'ldpi', ppi: 120 },
  { key: 'mdpi', ppi: 160 },
  { key: 'hdpi', ppi: 240 },
  { key: 'xhdpi', ppi: 320 },
  { key: 'xxhdpi', ppi: 480 },
  { key: 'xxxhdpi', ppi: 640 },
] as const

export type DensityClass = (typeof DENSITY_BUCKETS)[number]['key']

/** Ratios people recognise, used when the exact reduction is unreadable. */
const COMMON_RATIOS = [
  [1, 1],
  [5, 4],
  [4, 3],
  [3, 2],
  [8, 5],
  [16, 9],
  [2, 1],
  [21, 9],
  [32, 9],
] as const satisfies readonly (readonly [number, number])[]

/**
 * Resolutions people actually own, so the common case is two clicks rather than
 * remembering a pixel count.
 *
 * The names are industry standards (VESA/CTA display modes and marketing names),
 * not prose — they stay in every language, which is why they live here as data and
 * not in a translation catalog.
 */
export const RESOLUTION_PRESETS = [
  { name: 'HD', width: 1280, height: 720 },
  { name: 'WXGA', width: 1366, height: 768 },
  { name: 'Full HD', width: 1920, height: 1080 },
  { name: 'WUXGA', width: 1920, height: 1200 },
  { name: 'QHD', width: 2560, height: 1440 },
  { name: 'UWQHD', width: 3440, height: 1440 },
  { name: '4K UHD', width: 3840, height: 2160 },
  { name: '5K', width: 5120, height: 2880 },
  { name: '8K UHD', width: 7680, height: 4320 },
] as const satisfies readonly ResolutionPreset[]

export interface ResolutionPreset {
  readonly name: string
  readonly width: number
  readonly height: number
}

/**
 * The preset matching a resolution exactly, if any. Orientation counts: a rotated
 * panel is not the preset, and applying one must never silently flip the screen.
 */
export function findPreset(width: number, height: number): ResolutionPreset | undefined {
  return RESOLUTION_PRESETS.find((preset) => preset.width === width && preset.height === height)
}

export interface ScreenSpec {
  readonly horizontalPixels: number
  readonly verticalPixels: number
  /** Diagonal length of the panel, expressed in `unit`. */
  readonly diagonal: number
  readonly unit: LengthUnit
}

export interface AspectRatio {
  readonly width: number
  readonly height: number
  /** False when the true reduction was unreadable and a common ratio was chosen. */
  readonly exact: boolean
}

export interface ScreenMetrics {
  readonly ppi: number
  /** Distance between the centres of two neighbouring pixels. */
  readonly dotPitchMm: number
  /** Resolution measured corner to corner: √(w² + h²). */
  readonly diagonalPixels: number
  readonly totalPixels: number
  readonly megapixels: number
  readonly pixelsPerSquareInch: number
  readonly widthInches: number
  readonly heightInches: number
  readonly aspectRatio: AspectRatio
  readonly densityClass: DensityClass
}

export function toInches(length: number, unit: LengthUnit): number {
  return unit === 'cm' ? length / CM_PER_INCH : length
}

export function fromInches(inches: number, unit: LengthUnit): number {
  return unit === 'cm' ? inches * CM_PER_INCH : inches
}

export function isLengthUnit(value: unknown): value is LengthUnit {
  return LENGTH_UNITS.includes(value as LengthUnit)
}

/**
 * A spec is usable when both sides are whole pixels and the panel has a real size.
 * Anything else (blank field, zero, a fractional pixel count) has no meaningful
 * density, so the UI shows a hint instead of a number.
 */
export function isValidSpec(spec: ScreenSpec): boolean {
  const { horizontalPixels, verticalPixels, diagonal } = spec
  return (
    Number.isInteger(horizontalPixels) &&
    horizontalPixels > 0 &&
    Number.isInteger(verticalPixels) &&
    verticalPixels > 0 &&
    Number.isFinite(diagonal) &&
    diagonal > 0
  )
}

/** Returns `null` for a spec that cannot have a density — never throws. */
export function computeMetrics(spec: ScreenSpec): ScreenMetrics | null {
  if (!isValidSpec(spec)) return null

  const { horizontalPixels: width, verticalPixels: height } = spec
  const diagonalPixels = Math.hypot(width, height)
  const diagonalInches = toInches(spec.diagonal, spec.unit)
  const ppi = diagonalPixels / diagonalInches
  const totalPixels = width * height

  return {
    ppi,
    dotPitchMm: MM_PER_INCH / ppi,
    diagonalPixels,
    totalPixels,
    megapixels: totalPixels / 1_000_000,
    // Density in two dimensions: PPI across times PPI down.
    pixelsPerSquareInch: ppi * ppi,
    widthInches: width / ppi,
    heightInches: height / ppi,
    aspectRatio: aspectRatio(width, height),
    densityClass: classifyDensity(ppi),
  }
}

/** The bucket whose nominal PPI is closest to the real one. */
export function classifyDensity(ppi: number): DensityClass {
  let closest: DensityClass = DENSITY_BUCKETS[0].key
  let smallestGap = Infinity
  for (const bucket of DENSITY_BUCKETS) {
    const gap = Math.abs(ppi - bucket.ppi)
    if (gap < smallestGap) {
      smallestGap = gap
      closest = bucket.key
    }
  }
  return closest
}

/**
 * Reduces the resolution to a ratio, keeping the screen's orientation.
 *
 * The exact reduction is only useful when it stays small: 1366×768 reduces to
 * 683:384, which tells nobody anything, so a resolution like that is reported as
 * the nearest familiar ratio (16:9) and flagged as approximate.
 */
export function aspectRatio(width: number, height: number): AspectRatio {
  const long = Math.max(width, height)
  const short = Math.min(width, height)
  const divisor = greatestCommonDivisor(long, short)
  let pair: readonly [number, number] = [long / divisor, short / divisor]
  let exact = true

  if (pair[0] > MAX_REDUCED_SIDE || pair[1] > MAX_REDUCED_SIDE) {
    pair = nearestCommonRatio(long / short)
    exact = false
  }

  // Portrait screens report 9:16, not 16:9.
  const portrait = height > width
  return {
    width: portrait ? pair[1] : pair[0],
    height: portrait ? pair[0] : pair[1],
    exact,
  }
}

function nearestCommonRatio(value: number): readonly [number, number] {
  let closest: readonly [number, number] = COMMON_RATIOS[0]
  let smallestGap = Infinity
  for (const candidate of COMMON_RATIOS) {
    const gap = Math.abs(value - candidate[0] / candidate[1])
    if (gap < smallestGap) {
      smallestGap = gap
      closest = candidate
    }
  }
  return closest
}

function greatestCommonDivisor(a: number, b: number): number {
  let [big, small] = [a, b]
  while (small !== 0) [big, small] = [small, big % small]
  return big
}
